import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Upload, AlertTriangle } from 'lucide-react';
import { useSettings, formatNumber } from '../context/SettingsContext';
import { formatDate } from '../utils/dates';
import { getCancellations, importCancellations, type Cancellation, type CancellationImportResult } from '../api';
import { useToast } from '../context/ToastContext';
import { PageHeader, Button } from '../components/ui';
import Pagination from '../components/Pagination';

// Aflyste udlejninger med gebyr.
//
// De gemmes ikke som bookinger: en aflyst udlejning har hverken payout, faktura eller gennemført
// leje, og ville skulle filtreres fra i hver eneste sum. Kun dem med penge på gemmes — GoMore
// udsteder ingen kvittering på en rettidig aflysning, så de øvrige har intet at vise.
export default function CancellationsPage() {
  const { t } = useTranslation();
  const { settings } = useSettings();
  const { showError, showSuccess } = useToast();
  const fmt = (n: number) => formatNumber(n, settings.numberFormat);

  const [rows, setRows] = useState<Cancellation[]>([]);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState<CancellationImportResult | null>(null);
  const [page, setPage] = useState(1);
  const fileRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setRows(await getCancellations()); }
    catch (e) { showError(e instanceof Error ? e.message : 'Kunne ikke hente aflysninger'); }
    finally { setLoading(false); }
  }, [showError]);

  useEffect(() => { load(); }, [load]);

  const handleFile = async (file: File) => {
    setImporting(true);
    setResult(null);
    try {
      const parsed = JSON.parse(await file.text());
      // Scraperen pakker sit output som { results: [...] }; en rå liste accepteres også
      const list = Array.isArray(parsed) ? parsed : parsed.results ?? parsed.rentals;
      if (!Array.isArray(list)) throw new Error('Filen indeholder ingen udlejninger');
      const res = await importCancellations(list);
      setResult(res);
      showSuccess(`${res.created} nye, ${res.updated} opdateret`);
      await load();
    } catch (e) {
      showError(e instanceof Error ? e.message : 'Import fejlede');
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  // Gebyret kan gå begge veje: lejeren aflyser (indtægt) eller udlejer aflyser (udgift).
  // Kontoen netter, præcis som kontoudtoget gør.
  // SALGSMOMSEN BEREGNES, den læses ikke fra bilaget (besluttet 2026-08-19). GoMore undlader at
  // anføre moms på kunder uden for EU, men bookingen er indgået på dansk jord og gebyret er
  // momspligtigt her. Samme regel som i månedsrapporten — de to skal vise det samme tal.
  //
  // KØBSMOMSEN læses derimod fra bilaget: et fradrag kræver dokumentation, så vi trækker kun fra
  // hvad GoMore selv har anført. Den gemmes med samme fortegn som gebyret (negativt = penge ud),
  // og vises her som et positivt fradragsbeløb.
  const bookedVat = useCallback(
    (c: Cancellation) => Number(c.fee) * settings.vatRate / (100 + settings.vatRate),
    [settings.vatRate]
  );
  const totals = useMemo(() => {
    const sum = (f: (c: Cancellation) => number) => rows.reduce((s, c) => s + f(c), 0);
    return {
      netto:     sum(c => Number(c.fee)),
      salgsmoms: sum(c => c.vat_type === 'koebsmoms' ? 0 : bookedVat(c)),
      koebsmoms: sum(c => c.vat_type === 'koebsmoms' ? Math.abs(Number(c.vat_amount ?? 0)) : 0),
      tabtLeje:  sum(c => Number(c.rental_value ?? 0)),
    };
  }, [rows, bookedVat]);

  // Bilag uden momsoplysning skal stadig kunne ses. Momsen bogføres nu uanset, men afvigelsen
  // mellem bilag og bogføring er værd at kende — det kan være en manglende linje hos GoMore
  // snarere end en kunde uden for EU.
  const udenMoms = useMemo(() => rows.filter(c => c.vat_amount == null), [rows]);
  const udenPostering = useMemo(() => rows.filter(c => c.posting_id == null), [rows]);

  const pageSize = settings.itemsPerPage;
  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  const shown = Math.min(page, totalPages);
  const paged = rows.slice((shown - 1) * pageSize, shown * pageSize);

  return (
    <main className="max-w-7xl mx-auto px-6 md:px-10 py-10">
      <PageHeader
        eyebrow={t('nav.finance_group')}
        title="Aflysninger"
        subtitle="Aflyste udlejninger med gebyr"
        actions={
          <>
            <input ref={fileRef} type="file" accept="application/json" className="hidden"
              onChange={e => { const f = e.target.files?.[0]; if (f) handleFile(f); }} />
            <Button variant="primary" size="sm" iconLeft={<Upload size={14} />}
              disabled={importing} onClick={() => fileRef.current?.click()}>
              {importing ? 'Importerer…' : 'Importér fra GoMore'}
            </Button>
          </>
        }
      />

      {result && (
        <div className="mb-4 rounded-lg border border-ink-100 bg-cream-50 px-3 py-2 text-sm text-ink-700">
          <strong>{result.created}</strong> nye · <strong>{result.updated}</strong> opdateret ·{' '}
          <strong>{result.skipped}</strong> uden gebyr (gemmes ikke) · {result.total} i filen
          {result.errors.length > 0 && (
            <span className="text-rose-700"> · {result.errors.length} fejlede</span>
          )}
        </div>
      )}

      {!loading && (udenMoms.length > 0 || udenPostering.length > 0) && (
        <div className="mb-4 space-y-2">
          {udenMoms.length > 0 && (
            <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              <AlertTriangle size={15} className="mt-0.5 shrink-0" />
              <span>
                <strong>{udenMoms.length}</strong> {udenMoms.length === 1 ? 'kvittering oplyser' : 'kvitteringer oplyser'} ingen moms
                {' '}({udenMoms.map(c => c.renter_name ?? c.gomore_rental_id).join(', ')}).
                {' '}Sker for kunder uden for EU — men kan også være en manglende linje hos GoMore. Vi bogfører moms alligevel: bookingen er indgået på dansk jord, så gebyret er momspligtigt her.
              </span>
            </div>
          )}
          {udenPostering.length > 0 && (
            <div className="flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-900">
              <AlertTriangle size={15} className="mt-0.5 shrink-0" />
              <span>
                <strong>{udenPostering.length}</strong> uden en betaling i kontoudtoget — gebyret er ikke fundet som postering.
              </span>
            </div>
          )}
        </div>
      )}

      <div className="list-card">
        {loading ? (
          <div className="p-12 text-center text-ink-500">…</div>
        ) : rows.length === 0 ? (
          <div className="p-12 text-center text-ink-500">
            <p>Ingen aflysninger importeret endnu.</p>
            <p className="text-sm mt-1">Kør GoMore-scraperen og importér JSON-filen herover.</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th className="th">Fakturadato</th>
                    <th className="th">Faktura</th>
                    <th className="th">Lejer</th>
                    <th className="th">Bil</th>
                    <th className="th">Periode</th>
                    <th className="th !text-right">Tabt leje</th>
                    <th className="th !text-right">Gebyr</th>
                    <th className="th !text-right">Moms</th>
                  </tr>
                </thead>
                <tbody>
                  {paged.map(c => (
                    <tr key={c.id} className="row-hover">
                      <td className="px-4 py-3 text-ink-500 whitespace-nowrap">{c.invoice_date ? formatDate(c.invoice_date) : '—'}</td>
                      <td className="px-4 py-3 text-ink-500 text-xs font-mono">{c.invoice_number ?? '—'}</td>
                      <td className="px-4 py-3 text-ink-700 truncate max-w-[200px]" title={c.renter_name ?? ''}>{c.renter_name ?? '—'}</td>
                      <td className="px-4 py-3">
                        {c.registration_number
                          ? <span className="font-mono text-ink-900">{c.registration_number}</span>
                          : <span className="text-xs text-ink-300" title={c.vehicle_model ?? ''}>{c.vehicle_model ?? '—'}</span>}
                      </td>
                      <td className="px-4 py-3 text-ink-500 text-xs whitespace-nowrap">
                        {c.starts_at ? formatDate(c.starts_at) : '—'}
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-ink-500">{c.rental_value != null ? fmt(Number(c.rental_value)) : '—'}</td>
                      <td className={`px-4 py-3 text-right font-mono font-semibold ${Number(c.fee) < 0 ? 'text-rose-700' : 'text-ink-900'}`}>
                        {fmt(Number(c.fee))}
                      </td>
                      {/* Kolonnen viser den moms vi BOGFØRER, så den summerer til foden.
                          Salgsmoms beregnes; købsmoms læses fra bilaget. Afviger bilaget fra det
                          bogførte, markeres rækken — ellers ville forskellen være usynlig. */}
                      <td className="px-4 py-3 text-right font-mono text-ink-500">
                        {c.vat_type === 'koebsmoms' ? (
                          <>{fmt(Math.abs(Number(c.vat_amount ?? 0)))}<span className="ml-1 text-[10px] uppercase text-ink-400">køb</span></>
                        ) : c.vat_amount == null ? (
                          <span className="text-amber-600" title="Bilaget oplyser ingen moms — beregnet her, fordi gebyret er momspligtigt i Danmark">
                            {fmt(bookedVat(c))}<span className="ml-1 text-[10px] uppercase">beregnet</span>
                          </span>
                        ) : (
                          fmt(bookedVat(c))
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="text-xs font-medium text-ink-700">
                  <tr>
                    <td colSpan={5} className="px-4 py-3">I alt ({rows.length})</td>
                    <td className="px-4 py-3 text-right font-mono">{fmt(totals.tabtLeje)}</td>
                    <td className="px-4 py-3 text-right font-mono font-semibold text-ink-900">{fmt(totals.netto)}</td>
                    <td className="px-4 py-3 text-right font-mono">{fmt(totals.salgsmoms)}</td>
                  </tr>
                  <tr>
                    <td colSpan={8} className="px-4 pb-3 text-ink-500 font-normal">
                      Salgsmoms: <span className="text-ink-900 font-mono">{fmt(totals.salgsmoms)}</span>
                      {totals.koebsmoms !== 0 && (
                        <> · Købsmoms (egne aflysninger): <span className="text-ink-900 font-mono">{fmt(totals.koebsmoms)}</span></>
                      )}
                      <span className="text-ink-400"> — salgsmoms beregnet ({settings.vatRate}%), købsmoms læst fra bilaget</span>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-ink-100 text-sm text-ink-500">
                <span>{t('pagination.showing', {
                  from: (shown - 1) * pageSize + 1,
                  to: Math.min(shown * pageSize, rows.length),
                  total: rows.length,
                })}</span>
                <Pagination page={shown} totalPages={totalPages} onChange={setPage} />
              </div>
            )}
          </>
        )}
      </div>
    </main>
  );
}
